import { useEffect, useRef } from 'react'
import { useRevalidator } from 'react-router'

/**
 * Refresh the page's data when the visitor comes back to the tab (and, while
 * it stays visible, every few minutes) — so seats left and ticket status are
 * current without a live connection per visitor. A live stream would hold a
 * request open per open tab, which the single Fly machine's concurrency
 * limit (100) cannot afford for public pages.
 */
export function useRevalidateOnFocus(
	enabled = true,
	{ minAgeMs = 30_000, everyMs = 3 * 60_000 } = {},
) {
	const revalidator = useRevalidator()
	const lastRef = useRef(Date.now())
	const revalidateRef = useRef(revalidator.revalidate)
	revalidateRef.current = revalidator.revalidate

	useEffect(() => {
		if (!enabled || typeof document === 'undefined') return
		const refresh = () => {
			if (document.visibilityState !== 'visible') return
			if (Date.now() - lastRef.current < minAgeMs) return
			lastRef.current = Date.now()
			void revalidateRef.current()
		}
		const timer = setInterval(refresh, everyMs)
		document.addEventListener('visibilitychange', refresh)
		window.addEventListener('focus', refresh)
		return () => {
			clearInterval(timer)
			document.removeEventListener('visibilitychange', refresh)
			window.removeEventListener('focus', refresh)
		}
	}, [enabled, minAgeMs, everyMs])
}
